import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, Shield, Lock, CreditCard, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQty, clearCart } = useApp();

  const [step, setStep] = useState(cart.length ? 1 : 0);
  const [contact, setContact] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [payment, setPayment] = useState('card');
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvc: '' });

  const subtotal = cart.reduce((s, p) => s + p.price * p.qty, 0);
  const fee = subtotal > 0 ? 2.99 : 0;
  const total = subtotal + fee;

  if (cart.length === 0) {
    return (
      <div className="container-k py-16 text-center">
        <div className="text-5xl mb-3">🛒</div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Your cart is empty</h2>
        <p className="text-sm text-gray-500 mb-5">Browse experiences and add your favorites.</p>
        <Button onClick={() => navigate('/')} className="bg-xlook-orange hover:opacity-90 text-white rounded-full px-6">Explore activities</Button>
      </div>
    );
  }

  const handlePlaceOrder = (e) => {
    e.preventDefault();
    if (!contact.email || !contact.firstName) { toast.error('Please complete contact info'); return; }
    if (payment === 'card' && (!card.number || !card.name)) { toast.error('Please enter card details'); return; }
    const orderId = 'K' + Math.floor(Math.random() * 9000000 + 1000000);
    const order = { id: orderId, items: cart, total, contact, date: new Date().toISOString() };
    localStorage.setItem(`xlook_order_${orderId}`, JSON.stringify(order));
    clearCart();
    navigate(`/confirmation/${orderId}`);
  };

  return (
    <div className="container-k py-4 md:py-8">
      <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-gray-700 mb-2"><ChevronLeft className="w-4 h-4" /> Back</button>
      <h1 className="text-xl md:text-3xl font-extrabold text-gray-900 mb-1">Checkout</h1>
      <div className="text-sm text-gray-500 mb-4">Review your booking and complete payment</div>

      <div className="grid md:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-4">
          {/* Cart items */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <div className="font-semibold">Your bookings ({cart.length})</div>
            {cart.map((item) => (
              <div key={`${item.id}-${item.packageId}`} className="flex gap-3 pt-3 border-t border-gray-50 first:border-t-0 first:pt-0">
                <img src={item.image} alt={item.title} className="w-20 h-20 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500">{item.city}</div>
                  <div className="text-sm font-semibold line-clamp-2">{item.title}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{item.packageName}</div>
                  <div className="text-xs text-gray-600">Date: {item.date}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(item.id, item.packageId, item.qty - 1)} className="w-7 h-7 border rounded-md flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                      <span className="text-sm font-semibold w-5 text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.packageId, item.qty + 1)} className="w-7 h-7 border rounded-md flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      <button onClick={() => removeFromCart(item.id, item.packageId)} className="text-gray-500 hover:text-red-500 ml-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="text-sm font-extrabold" style={{ color: '#ff5b00' }}>${(item.price * item.qty).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact */}
          <form onSubmit={handlePlaceOrder} className="bg-white border border-gray-100 rounded-xl p-4 space-y-4">
            <div className="font-semibold">Contact details</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First name</Label>
                <Input value={contact.firstName} onChange={(e) => setContact({ ...contact, firstName: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Last name</Label>
                <Input value={contact.lastName} onChange={(e) => setContact({ ...contact, lastName: e.target.value })} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Phone</Label>
                <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div className="font-semibold pt-2">Payment method</div>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-2">
              <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${payment === 'card' ? 'border-xlook-orange bg-orange-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="card" id="card" />
                <CreditCard className="w-4 h-4" />
                <span className="text-sm">Credit / Debit Card</span>
              </label>
              <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${payment === 'paypal' ? 'border-xlook-orange bg-orange-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="paypal" id="paypal" />
                <span className="text-sm">PayPal</span>
              </label>
              <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${payment === 'alipay' ? 'border-xlook-orange bg-orange-50' : 'border-gray-200'}`}>
                <RadioGroupItem value="alipay" id="alipay" />
                <span className="text-sm">Alipay / WeChat Pay</span>
              </label>
            </RadioGroup>

            {payment === 'card' && (
              <div className="space-y-3 pt-2">
                <div>
                  <Label className="text-xs">Card number</Label>
                  <Input value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} placeholder="1234 5678 9012 3456" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Name on card</Label>
                  <Input value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Expiry</Label>
                    <Input value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} placeholder="MM/YY" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">CVC</Label>
                    <Input value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} placeholder="123" className="mt-1" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-gray-500 pt-2"><Lock className="w-3 h-3" /> Your payment is encrypted and secure</div>
            <Button type="submit" className="w-full rounded-full bg-xlook-orange hover:opacity-90 text-white h-12 font-semibold">Place order · ${total.toFixed(2)}</Button>
          </form>
        </div>

        {/* Summary */}
        <aside className="md:block">
          <div className="md:sticky md:top-28 bg-white border border-gray-100 rounded-xl p-4">
            <div className="font-semibold mb-3">Order summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Service fee</span><span>${fee.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Taxes</span><span>Included</span></div>
              <div className="h-px bg-gray-100 my-2" />
              <div className="flex justify-between font-extrabold text-base"><span>Total</span><span style={{ color: '#ff5b00' }}>${total.toFixed(2)}</span></div>
            </div>
            <div className="mt-3 text-xs text-gray-500 flex items-start gap-1"><Shield className="w-3.5 h-3.5 mt-0.5" /> Free cancellation available for most bookings</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
